/**
 * AppText — drop-in replacements for React Native's Text and TextInput that
 * apply the app typeface (Plus Jakarta Sans).
 *
 * Each weight is registered as its own font family (see App.tsx), so the
 * family is picked from the final flattened fontWeight and fontWeight is reset
 * to 'normal' — this avoids faux-bold on web and missing weights on Android.
 * Styles that set their own fontFamily (e.g. icon fonts) are left untouched.
 */
import React, { forwardRef } from 'react';
import {
  StyleSheet,
  Text as RNText,
  TextInput as RNTextInput,
  type StyleProp,
  type TextInputProps,
  type TextProps,
  type TextStyle,
} from 'react-native';
import { fontFamilyForWeight } from '../theme';

function withAppFont(style: StyleProp<TextStyle>): StyleProp<TextStyle> {
  const flat = (StyleSheet.flatten(style) ?? {}) as TextStyle;
  if (flat.fontFamily) return style;
  return [style, { fontFamily: fontFamilyForWeight(flat.fontWeight), fontWeight: 'normal' }];
}

export const Text = forwardRef<RNText, TextProps>(function AppText(props, ref) {
  return <RNText ref={ref} {...props} style={withAppFont(props.style)} />;
});

export const TextInput = forwardRef<RNTextInput, TextInputProps>(function AppTextInput(props, ref) {
  return <RNTextInput ref={ref} {...props} style={withAppFont(props.style)} />;
});

export type Text = RNText;
export type TextInput = RNTextInput;
